import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { createSelector } from '@reduxjs/toolkit'
import { Button, cn, Heading, Label, MergeIcon, Paragraph, showToast } from '@quickmd-llc/design-system'
import { PageLoadingSpinner, useUpdateEffect } from 'ui_kit_qmd'
import { ADMIN_EVENT_NAMES, tracking } from '@/lib/segment/tracking'
import { useAppDispatch } from '@/redux/hooks/useAppDispatch'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import { type QuickMdState } from '@/app/store'
import { selectMergeDuplicateAccounts } from '@/store/flags/flagsSelectors'
import { MERGE_PATIENT_ACCOUNTS_ROUTE, PATIENT_CHARTS_TEXT } from '@/utils/constants'
import { getChartPatientsList } from '@/store/charts/ChartsThunk'
import { setChartsError, setPatientsList } from '@/store/charts/ChartsActions'
import { getTabId, parseTimeZone } from '@/utils/common'
import { ChartListener } from '@/store/charts/ChartListener'
import { getMediaFile } from '@/store/file-pop-up/FilePopUpThunk'
import { getIDVerification } from '@/store/id-verification-pop-up/IDVerificationPopUpThunk'
import { useChangeSearchParams } from '@/utils/useChangeSearchParams'
import { SearchComponent } from '@/components/SearchComponent/SearchComponent'
import { buildSearchQuery } from './buildSearchQuery'
import { count, routeParam, text } from './constants'
import EmptyState from './EmptyState'
import { type DrugScreenFilePayload, PatientChartRow } from './components/PatientChartRow'
import { useRecentlyViewedCharts } from './useRecentlyViewedCharts'
import { type PatientChartListItem } from './types'

// ChartListener requires callback props for the patient-chart detail page; on
// the list it only relays GET_PATIENTS_LIST_SUCCESS, so the rest are no-ops.
const noop = () => {}

const selectMergeFlag = createSelector([selectMergeDuplicateAccounts], (mergeDuplicateAccounts) => ({
  hasMergeDuplicateAccounts: mergeDuplicateAccounts?.isActive,
}))

const selectAdminProfile = (state: QuickMdState) => state.accountSettings.adminProfile
const selectChartsError = (state: QuickMdState) => state.chartsReducer.error
const selectChartsLoading = (state: QuickMdState) => state.chartsReducer.loading
const selectNewPatientsList = (state: QuickMdState) => state.chartsReducer.newPatientsList
const selectPatientsList = (state: QuickMdState) => state.chartsReducer.patientsList

export const PatientChartsPage = memo(() => {
  const dispatch = useAppDispatch()
  const listInnerRef = useRef<HTMLDivElement>(null)
  const tabId = getTabId()

  const adminProfile = useAppSelector(selectAdminProfile)
  const { hasMergeDuplicateAccounts } = useAppSelector(selectMergeFlag)

  const error = useAppSelector(selectChartsError)
  const loading = useAppSelector(selectChartsLoading)
  const newPatientsList: PatientChartListItem[] | null = useAppSelector(selectNewPatientsList)
  const patientsList: PatientChartListItem[] = useAppSelector(selectPatientsList) ?? []

  const recentlyViewed = useRecentlyViewedCharts()
  const { onChangeSearchParam, searchParams } = useChangeSearchParams()

  const [offSet, setOffSet] = useState(0)
  const [searchInputValue, setSearchInputValue] = useState(() => searchParams.get(routeParam.SEARCH_VALUE) ?? '')

  const userTimeZone = useMemo(() => parseTimeZone(adminProfile?.TimeZone), [adminProfile])

  const runSearch = useCallback(
    (value: string) => {
      setOffSet(0)
      dispatch(setPatientsList([]))

      const parsed = buildSearchQuery(value)
      if (!parsed.isSearchable) {
        return
      }

      const action = getChartPatientsList({
        offSet: 0,
        count: count.GET_PATIENTS_LIST_COUNT,
        searchStringCondition: parsed.searchStringCondition,
        birthDate: parsed.birthDate,
        isUpdatePatientsList: true,
      })
      if (action) {
        dispatch(action)
      }
    },
    [dispatch],
  )

  // Keep a stable, always-current reference to `runSearch` for the debounced
  // caller and the mount effect so neither re-subscribes as state changes.
  const runSearchRef = useRef(runSearch)
  useEffect(() => {
    runSearchRef.current = runSearch
  }, [runSearch])

  const searchInputValueRef = useRef(searchInputValue)
  useEffect(() => {
    searchInputValueRef.current = searchInputValue
  }, [searchInputValue])

  const debouncedRunSearch = useMemo(() => debounce((value: string) => runSearchRef.current(value), 500), [])
  useEffect(() => () => debouncedRunSearch.cancel(), [debouncedRunSearch])

  // On mount, restore a search carried in the URL (e.g. returning from a chart
  // via the breadcrumb) and clear the list when leaving the page.
  useEffect(() => {
    const initialValue = searchInputValueRef.current
    if (buildSearchQuery(initialValue).isSearchable) {
      runSearchRef.current(initialValue)
    }

    return () => {
      dispatch(setPatientsList([]))
    }
  }, [dispatch])

  // Infinite-scroll pagination for search results.
  useEffect(() => {
    if (!offSet) {
      return
    }

    const parsed = buildSearchQuery(searchInputValueRef.current)
    if (!parsed.isSearchable) {
      return
    }

    const action = getChartPatientsList({
      offSet,
      count: count.GET_PATIENTS_LIST_COUNT,
      searchStringCondition: parsed.searchStringCondition,
      birthDate: parsed.birthDate,
      isUpdatePatientsList: false,
    })
    if (action) {
      dispatch(action)
    }
  }, [offSet, dispatch])

  // Merge live row updates pushed via ChartListener into the visible list.
  useUpdateEffect(() => {
    if (!newPatientsList) {
      return
    }

    const entities = new Map<string, PatientChartListItem>()
    patientsList.forEach((patient) => entities.set(patient.PatientId, patient))
    newPatientsList.forEach((patient) => {
      if (entities.has(patient.PatientId)) {
        entities.set(patient.PatientId, patient)
      }
    })

    dispatch(setPatientsList(Array.from(entities.values())))
  }, [newPatientsList])

  useEffect(() => {
    if (!error) {
      return
    }

    showToast({ type: 'error', message: error, position: 'bottom-right' })
    dispatch(setChartsError(''))
  }, [error, dispatch])

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInputValue(value)
      // Replaced rather than pushed: a search box is not navigation, and pushing
      // per keystroke made leaving the page take one Back press per character.
      onChangeSearchParam(
        { key: routeParam.SEARCH_VALUE, value: buildSearchQuery(value).isSearchable ? value : '' },
        { replace: true },
      )
      debouncedRunSearch(value)
    },
    [debouncedRunSearch, onChangeSearchParam],
  )

  const handleClearSearch = useCallback(() => {
    debouncedRunSearch.cancel()
    setSearchInputValue('')
    setOffSet(0)
    onChangeSearchParam(undefined, { replace: true })
    dispatch(setPatientsList([]))
  }, [debouncedRunSearch, dispatch, onChangeSearchParam])

  // SearchComponent reports changes in event shape. An emptied field — whether cleared with the
  // X or backspaced to nothing — takes the clear path so the results drop immediately instead of
  // lingering for the debounce interval.
  const handleSearchTextChange = useCallback(
    ({ target: { value } }: { target: { value: string } }) => {
      if (value) {
        handleSearchChange(value)
      } else {
        handleClearSearch()
      }
    },
    [handleSearchChange, handleClearSearch],
  )

  const onScroll = useCallback(() => {
    const node = listInnerRef.current
    if (!node || !buildSearchQuery(searchInputValueRef.current).isSearchable) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = node
    if (scrollHeight - (scrollTop + clientHeight) < 20 && !(patientsList.length % count.GET_PATIENTS_LIST_COUNT)) {
      setOffSet(patientsList.length)
    }
  }, [patientsList.length])

  const handleOpenDrugScreenFile = useCallback(
    (payload: DrugScreenFilePayload) => {
      dispatch(getMediaFile(payload, tabId))
    },
    [dispatch, tabId],
  )

  const handleOpenIdVerification = useCallback(
    (patientId: string) => {
      dispatch(getIDVerification(patientId, tabId))
    },
    [dispatch, tabId],
  )

  const parsedSearch = useMemo(() => buildSearchQuery(searchInputValue), [searchInputValue])
  const isSearching = parsedSearch.isSearchable
  const rows: PatientChartListItem[] = isSearching ? patientsList : recentlyViewed
  const matchCount = patientsList.length

  const headerTitle = isSearching ? text.SEARCH_RESULTS_TITLE : text.RECENTLY_VIEWED_TITLE

  // Spelling out each half separately is the point: it shows whether a typed date
  // was read as a date of birth or left in the free text.
  const searchedTerms = [
    parsedSearch.searchStringCondition ? `“${parsedSearch.searchStringCondition}”` : '',
    parsedSearch.displayDate ? `${text.SEARCH_DOB_PREFIX} ${parsedSearch.displayDate}` : '',
  ]
    .filter(Boolean)
    .join(text.SEARCH_TERM_SEPARATOR)

  const searchSubtitle = `${matchCount} ${matchCount === 1 ? 'match' : 'matches'} for ${searchedTerms}`
  const recentSubtitle = recentlyViewed.length ? text.RECENTLY_VIEWED_SUBTITLE : text.NO_CHARTS_VIEWED_SUBTITLE
  const invalidDateSubtitle = `“${parsedSearch.invalidDate}” ${text.INVALID_DOB_MESSAGE}`

  const headerSubtitle =
    parsedSearch.invalidDate ? invalidDateSubtitle
    : isSearching ? searchSubtitle
    : recentSubtitle

  const showPlaceholder =
    parsedSearch.invalidDate ? false
    : isSearching ? !loading && matchCount === 0
    : recentlyViewed.length === 0
  const placeholder = <EmptyState {...(isSearching ? { searchQuery: searchInputValue } : {})} />

  return (
    <>
      <ChartListener
        patientId={undefined}
        tabId={undefined}
        onClearForm={noop}
        closeAlertSection={noop}
        openPastVisits={noop}
        openUpcomingVisits={noop}
        openFiles={noop}
        closeTagsPopUp={noop}
        closeUpload={noop}
      />

      {loading ?
        <PageLoadingSpinner />
      : null}

      <div className='grid h-lvh gap-sp-3 overflow-x-auto bg-background p-sp-6 [grid-template-rows:auto_1fr]'>
        <div className='flex items-center justify-between'>
          <Heading level='h1'>{PATIENT_CHARTS_TEXT}</Heading>
        </div>

        <div className='flex min-h-0 min-w-[768px] flex-col rounded-large border border-border-medium bg-white'>
          <div className='flex items-center justify-between gap-sp-2 border-b border-border-light p-sp-2'>
            <div className='flex min-w-[14rem] shrink-[100] flex-col gap-sp-0.5'>
              <Label size='xl' className='text-text-dark'>
                {headerTitle}
              </Label>
              <Paragraph size='sm' className='line-clamp-2 text-text-medium'>
                {headerSubtitle}
              </Paragraph>
            </div>

            <div className='flex min-w-0 items-center gap-sp-2'>
              <div className='w-96 min-w-0'>
                <SearchComponent
                  placeholder={text.SEARCH_PLACEHOLDER}
                  searchText={searchInputValue}
                  onChangeSearchText={handleSearchTextChange}
                  error={Boolean(parsedSearch.invalidDate)}
                  additionalClassName={cn('w-full')}
                />
              </div>

              {hasMergeDuplicateAccounts ?
                <Button
                  to={MERGE_PATIENT_ACCOUNTS_ROUTE}
                  priority='secondary'
                  className='shrink-0 whitespace-nowrap'
                  onClick={() => tracking.trackEvent(ADMIN_EVENT_NAMES.PATIENT_CHARTS_MERGE_ACCOUNTS)}>
                  <MergeIcon />
                  {text.MERGE_ACCOUNTS}
                </Button>
              : null}
            </div>
          </div>

          <div ref={listInnerRef} onScroll={onScroll} className='min-h-0 flex-1 overflow-y-auto'>
            {showPlaceholder ?
              placeholder
            : rows.map((patient) => (
                <PatientChartRow
                  key={patient.PatientId}
                  patient={patient}
                  userTimeZone={userTimeZone}
                  onOpenDrugScreenFile={handleOpenDrugScreenFile}
                  onOpenIdVerification={handleOpenIdVerification}
                />
              ))
            }
          </div>
        </div>
      </div>
    </>
  )
})
